import React, { useState, useEffect } from 'react';
import { THEME } from '../../styles/theme';
import { getJarvisMemoryData, getRules, getBehaviors, getPatterns, getTrades } from '../../services/storage';
import { EVOLUTION_STAGES } from '../../engines/predictionTracker';

// Boot sequence steps — each takes ~400ms
const BOOT_STEPS = [
  { id: 'storage',    label: 'STORAGE ENGINE',     icon: '🗄️' },
  { id: 'encryption', label: 'ENCRYPTION MODULE',  icon: '🔐' },
  { id: 'knowledge',  label: 'KNOWLEDGE BASE',      icon: '🧠' },
  { id: 'rules',      label: 'RULE ENGINE',         icon: '📋' },
  { id: 'brain',      label: 'SELF-BRAIN',          icon: '⚡' },
  { id: 'ai',         label: 'GROQ AI CORE',        icon: '🤖' },
  { id: 'market',     label: 'MARKET FEED',         icon: '📊' },
  { id: 'ready',      label: 'SYSTEM READY',        icon: '✅' },
];

const S = {
  root: {
    position:       'fixed',
    inset:          0,
    background:     THEME.colors.bg,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    fontFamily:     THEME.fonts.hud,
    overflow:       'hidden',
    zIndex:         9999,
    // Never had ANY horizontal padding, so on mobile the text below sat
    // flush against both screen edges with nothing to give.
    padding:        '0 18px',
    boxSizing:      'border-box',
  },
  grid: {
    position:   'absolute',
    inset:      0,
    opacity:    0.04,
    backgroundImage: 'linear-gradient(rgba(0,212,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,1) 1px, transparent 1px)',
    backgroundSize:  '40px 40px',
    pointerEvents: 'none',
  },
  glowRing: (visible) => ({
    position:     'absolute',
    width:        '400px',
    height:       '400px',
    borderRadius: '50%',
    border:       `1px solid rgba(0,212,255,0.15)`,
    boxShadow:    visible ? '0 0 80px rgba(0,212,255,0.1)' : 'none',
    transition:   'box-shadow 1s ease',
    pointerEvents: 'none',
  }),
  logoWrap: {
    textAlign:  'center',
    marginBottom: '48px',
    position:   'relative',
    zIndex:     1,
    // The critical fix: a flex item's default min-width is 'auto', which
    // means it refuses to shrink below its content's natural (unwrapped)
    // width — this is exactly why the long tagline/stage strings below
    // were overflowing past both screen edges instead of wrapping. S.root
    // is display:flex, so every direct child needs this.
    minWidth:   0,
    maxWidth:   '100%',
  },
  logoText: (visible) => ({
    // clamp() so a ~360px budget phone and a desktop window both get a
    // sensible size instead of one fixed 52px/16px that only worked on
    // wide screens — this specific string ("J.A.R.V.I.S", 16px tracking)
    // was the worst offender, easily 500px+ wide at the old fixed size.
    fontSize:      'clamp(26px, 9vw, 52px)',
    fontWeight:    '900',
    letterSpacing: 'clamp(4px, 3vw, 16px)',
    color:         THEME.colors.blue,
    textShadow:    visible ? `0 0 40px ${THEME.colors.blue}88` : 'none',
    transition:    'text-shadow 1s ease',
  }),
  tagline: {
    fontSize:      'clamp(9px, 2.6vw, 11px)',
    letterSpacing: 'clamp(1px, 1vw, 4px)',
    color:         THEME.colors.textMuted,
    marginTop:     '8px',
  },
  stageTag: {
    marginTop:     '12px',
    fontSize:      'clamp(10px, 2.8vw, 12px)',
    color:         THEME.colors.gold,
    letterSpacing: 'clamp(0.5px, 0.6vw, 2px)',
    fontWeight:    '700',
  },
  statsRow: {
    display:       'flex',
    gap:           '32px',
    marginBottom:  '32px',
    position:      'relative',
    zIndex:        1,
    minWidth:      0,
    maxWidth:      '100%',
    flexWrap:      'wrap',
    justifyContent: 'center',
  },
  stat: {
    textAlign: 'center',
  },
  statVal: {
    fontSize:      '20px',
    fontWeight:    '700',
    color:         THEME.colors.blue,
    letterSpacing: '2px',
  },
  statLabel: {
    fontSize:      '10px',
    color:         THEME.colors.textMuted,
    letterSpacing: '2px',
    marginTop:     '4px',
  },
  stepsWrap: {
    // was a hard width:'340px' — fine on a ~412px phone, but with
    // S.root's new 18px*2 padding a ~360px budget phone only has 324px
    // left, so a fixed 340px would still overflow there.
    width:         '100%',
    maxWidth:      '340px',
    boxSizing:     'border-box',
    position:      'relative',
    zIndex:        1,
    marginBottom:  '28px',
  },
  step: (done, active) => ({
    display:       'flex',
    alignItems:    'center',
    gap:           '12px',
    padding:       '6px 0',
    opacity:       done || active ? 1 : 0.25,
    transition:    'opacity 0.3s ease',
  }),
  stepDot: (done, active) => ({
    width:         '8px',
    height:        '8px',
    borderRadius:  '50%',
    flexShrink:    0,
    background:    done    ? THEME.colors.green
                 : active  ? THEME.colors.blue
                 :           'rgba(255,255,255,0.15)',
    boxShadow:     active  ? `0 0 8px ${THEME.colors.blue}` : 'none',
    transition:    'all 0.3s',
  }),
  stepLabel: (done, active) => ({
    fontSize:      '12px',
    letterSpacing: '2px',
    color:         done    ? THEME.colors.green
                 : active  ? THEME.colors.blue
                 :           THEME.colors.textMuted,
    flex:          1,
    minWidth:      0, // same flex min-width:auto guard as logoWrap above
  }),
  stepIcon: {
    fontSize: '14px',
  },
  progressBar: {
    width:         '100%',
    maxWidth:      '340px',
    boxSizing:     'border-box',
    height:        '2px',
    background:    'rgba(255,255,255,0.08)',
    borderRadius:  '2px',
    overflow:      'hidden',
    position:      'relative',
    zIndex:        1,
  },
  progressFill: (pct) => ({
    height:        '100%',
    width:         `${pct}%`,
    background:    `linear-gradient(90deg, ${THEME.colors.blue}, ${THEME.colors.green})`,
    borderRadius:  '2px',
    transition:    'width 0.4s ease',
    boxShadow:     `0 0 8px ${THEME.colors.blue}88`,
  }),
  skipHint: {
    marginTop:     '28px',
    fontSize:      '11px',
    color:         'rgba(255,255,255,0.2)',
    letterSpacing: '2px',
    cursor:        'pointer',
    position:      'relative',
    zIndex:        1,
  },
};

export default function BootScreen({ onComplete }) {
  const [stepIdx, setStepIdx]   = useState(0);   // which step is active
  const [doneSteps, setDone]    = useState([]);   // completed step IDs
  const [glowOn, setGlowOn]     = useState(false);

  // Snapshot JARVIS stats for display
  const memory   = getJarvisMemoryData();
  const stage    = memory.evolution_stage || 1;
  const stageName = EVOLUTION_STAGES[stage]?.name || 'Student';
  const rules    = getRules().filter(r => r.status === 'active').length;
  const behaviors = getBehaviors().length;
  const patterns  = getPatterns().length;
  const trades    = getTrades().length;

  useEffect(() => {
    setTimeout(() => setGlowOn(true), 300);
  }, []);

  useEffect(() => {
    if (stepIdx >= BOOT_STEPS.length) {
      // All steps done — wait a beat then hand off
      const t = setTimeout(onComplete, 600);
      return () => clearTimeout(t);
    }

    const delay = stepIdx === 0 ? 600 : 380;
    const t = setTimeout(() => {
      setDone(prev => [...prev, BOOT_STEPS[stepIdx].id]);
      setStepIdx(prev => prev + 1);
    }, delay);

    return () => clearTimeout(t);
  }, [stepIdx, onComplete]);

  const progress = Math.round((doneSteps.length / BOOT_STEPS.length) * 100);
  const allDone  = doneSteps.length === BOOT_STEPS.length;

  return (
    <div style={S.root} onClick={() => stepIdx < BOOT_STEPS.length && setStepIdx(BOOT_STEPS.length - 1)}>
      <div style={S.grid} />
      <div style={S.glowRing(glowOn)} />

      {/* Logo */}
      <div style={S.logoWrap}>
        <div style={S.logoText(glowOn)}>J.A.R.V.I.S</div>
        <div style={S.tagline}>JUST A RATHER VERY INTELLIGENT SYSTEM</div>
        <div style={S.stageTag}>XAU/USD TRADING AI  ·  STAGE {stage} — {stageName.toUpperCase()}</div>
      </div>

      {/* Knowledge stats */}
      <div style={S.statsRow}>
        {[
          { val: rules,       label: 'RULES'    },
          { val: behaviors,   label: 'BEHAVIORS'},
          { val: patterns,    label: 'PATTERNS' },
          { val: trades,      label: 'TRADES'   },
        ].map(s => (
          <div style={S.stat} key={s.label}>
            <div style={S.statVal}>{s.val}</div>
            <div style={S.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Boot steps */}
      <div style={S.stepsWrap}>
        {BOOT_STEPS.map((step, i) => {
          const done   = doneSteps.includes(step.id);
          const active = stepIdx === i && !allDone;
          return (
            <div style={S.step(done, active)} key={step.id}>
              <div style={S.stepDot(done, active)} />
              <div style={S.stepLabel(done, active)}>{step.label}</div>
              <div style={S.stepIcon}>{done ? '✓' : step.icon}</div>
            </div>
          );
        })}
      </div>

      {/* Progress */}
      <div style={S.progressBar}>
        <div style={S.progressFill(progress)} />
      </div>

      <div style={S.skipHint}>TAP TO SKIP</div>
    </div>
  );
}
